import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Grid, Card, CardContent, CardActions, Alert, Snackbar,
  Tooltip, Tab, Tabs, InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TableChart as TableIcon,
  Storage as StorageIcon,
  Diamond as DiamondIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CafeTables = ({keywordsData, language}) => {
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    table_number: '',
    table_name: '',
    capacity: 4,
    location: 'Indoor',
    is_active: true,
    is_occupied: false,
    is_vip: false,
    min_charge: 0,
    notes: ''
  });

  // Bulk create form
  const [bulkData, setBulkData] = useState({
    startNumber: 1,
    endNumber: 5,
    location: 'Indoor',
    capacity: 4,
    is_vip: false,
    min_charge: 0
  });

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/cafe-tables');
      setTables(response.data.tables || []);
    } catch (error) {
      console.error('Error fetching tables:', error);
      showSnackbar('Error fetching tables', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleBulkInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBulkData({
      ...bulkData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCreateTable = async () => {
    try {
      const response = await api.post('/admin/cafe-tables', formData);
      showSnackbar('Table created successfully');
      setOpenDialog(false);
      resetForm();
      fetchTables();
    } catch (error) {
      console.error('Error creating table:', error);
      showSnackbar(error.response?.data?.message || 'Error creating table', 'error');
    }
  };

  const handleBulkCreate = async () => {
    try {
      const response = await api.post('/admin/cafe-tables/bulk', bulkData);
      showSnackbar(response.data.message);
      setOpenBulkDialog(false);
      fetchTables();
    } catch (error) {
      console.error('Error bulk creating tables:', error);
      showSnackbar(error.response?.data?.message || 'Error creating tables', 'error');
    }
  };

  const handleUpdateTable = async () => {
    try {
      const response = await api.put(`/admin/cafe-tables/${selectedTable.id}`, formData);
      showSnackbar('Table updated successfully');
      setOpenDialog(false);
      resetForm();
      fetchTables();
    } catch (error) {
      console.error('Error updating table:', error);
      showSnackbar(error.response?.data?.message || 'Error updating table', 'error');
    }
  };

  const handleDeleteTable = async (id) => {
    if (!window.confirm('Are you sure you want to delete this table?')) return;
    
    try {
      await api.delete(`/admin/cafe-tables/${id}`);
      showSnackbar('Table deleted successfully');
      fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      showSnackbar('Error deleting table', 'error');
    }
  };

  const handleRegenerateQR = async (table) => {
    try {
      const response = await api.post(`/admin/cafe-tables/${table.id}/regenerate-qr`);
      setQrCodeImage(response.data.qr_code.image);
      setSelectedTable(table);
      setOpenQRDialog(true);
      showSnackbar('QR code regenerated successfully');
      fetchTables();
    } catch (error) {
      console.error('Error regenerating QR:', error);
      showSnackbar('Error regenerating QR code', 'error');
    }
  };

  const handleViewQR = (table) => {
    setSelectedTable(table);
    setQrCodeImage(table.qr_code_image);
    setOpenQRDialog(true);
  };

  const handleDownloadQR = () => {
    if (!qrCodeImage) return;
    
    const link = document.createElement('a');
    link.href = qrCodeImage;
    link.download = `table_${selectedTable.table_number}_qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
    setFormData({
      table_number: '',
      table_name: '',
      capacity: 4,
      location: 'Indoor',
      is_active: true,
      is_occupied: false,
      is_vip: false,
      min_charge: 0,
      notes: ''
    });
    setSelectedTable(null);
  };

  const openEditDialog = (table) => {
    setSelectedTable(table);
    setFormData({
      table_number: table.table_number,
      table_name: table.table_name,
      capacity: table.capacity,
      location: table.location,
      is_active: table.is_active,
      is_occupied: table.is_occupied,
      is_vip: table.is_vip || false,
      min_charge: table.min_charge || 0,
      notes: table.notes || ''
    });
    setOpenDialog(true);
  };

  // Calculate statistics
  const totalTables = tables.length;
  const activeTables = tables.filter(t => t.is_active).length;
  const occupiedTables = tables.filter(t => t.is_occupied).length;
  const vipTables = tables.filter(t => t.is_vip).length;
  const totalCapacity = tables.reduce((sum, t) => sum + t.capacity, 0);

  return (
    <Container maxWidth="xl" sx={{ py: 4, mb: 10, backgroundColor: '#000000', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TableIcon sx={{ fontSize: 40, color: '#FFD700' }} />
          <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 900 }}>
            {language === 'ar' ? 'إدارة طاولات المقهى' : 'Cafe Tables Management'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<StorageIcon />}
            onClick={() => setOpenBulkDialog(true)}
            sx={{
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              border: '1px solid #2D2D2D',
              '&:hover': {
                borderColor: '#FFD700',
                backgroundColor: '#2D2D2D'
              }
            }}
          >
            {language === 'ar' ? 'إنشاء بكميات كبيرة' : 'Bulk Create'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#FFE55C'
              }
            }}
          >
            {language === 'ar' ? 'إضافة طاولة جديد' : 'Add New Table'}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إجمالي الطاولات' : 'Total Tables'}</Typography>
            <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 800 }}>{totalTables}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الطاولات المتاحة' : 'Active Tables'}</Typography>
            <Typography variant="h3" sx={{ color: '#4CAF50', fontWeight: 800 }}>{activeTables}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الطاولات المشغولة' : 'Occupied'}</Typography>
            <Typography variant="h3" sx={{ color: '#FF3D00', fontWeight: 800 }}>{occupiedTables}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'طاولات VIP' : 'VIP Tables'}</Typography>
            <Typography variant="h3" sx={{ color: '#9C27B0', fontWeight: 800 }}>{vipTables}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'السعة الإجمالية' : 'Total Capacity'}</Typography>
            <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 800 }}>{totalCapacity}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tables List */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'رقم الطاولة #' : 'Table #'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'إسم الطاولة' : 'Name'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'السعة' : 'Capacity'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الموقع' : 'Location'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'VIP' : 'VIP'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الحد الأدنى' : 'Min Charge'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'كود المسح' : 'QR Code'}</TableCell>
              <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tables.map((table) => (
              <TableRow key={table.id} sx={{ '&:hover': { backgroundColor: '#1A1A1A' } }}>
                <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>{table.table_number}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{table.table_name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{table.capacity}</TableCell>
                <TableCell>
                  <Chip 
                    label={table.location}
                    sx={{
                      backgroundColor: table.location === 'VIP' ? '#9C27B0' : '#1A1A1A',
                      color: table.location === 'VIP' ? '#FFFFFF' : '#FFFFFF',
                      border: '1px solid #2D2D2D'
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {table.is_vip ? (
                    <Chip 
                      icon={<DiamondIcon sx={{ fontSize: 14 }} />}
                      label="VIP"
                      sx={{
                        backgroundColor: '#9C27B0',
                        color: '#FFFFFF',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                  ) : (
                    <Chip 
                      label="-"
                      sx={{ backgroundColor: '#1A1A1A', color: '#B3B3B3' }}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {table.min_charge > 0 ? (
                    <Chip 
                      icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                      label={`${table.min_charge} ${language === 'ar' ? 'ر.م' : 'R.m'}`}
                      sx={{
                        backgroundColor: 'rgba(156, 39, 176, 0.2)',
                        color: '#CE93D8',
                        border: '1px solid #9C27B0'
                      }}
                      size="small"
                    />
                  ) : (
                    <Chip 
                      label="-"
                      sx={{ backgroundColor: '#1A1A1A', color: '#B3B3B3' }}
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={table.is_active ? language === 'ar' ? 'مفعل' : 'Active' : language === 'ar' ? 'غير مفعل' : 'Inactive'}
                      sx={{
                        backgroundColor: table.is_active ? '#4CAF50' : '#2D2D2D',
                        color: table.is_active ? '#000000' : '#B3B3B3'
                      }}
                      size="small"
                    />
                    <Chip 
                      label={table.is_occupied ? language === 'ar' ? 'مشغولة' : 'Occupied' : language === 'ar' ? 'متاحة' : 'Available'}
                      sx={{
                        backgroundColor: table.is_occupied ? '#FF3D00' : '#2196F3',
                        color: '#FFFFFF'
                      }}
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={language === 'ar' ? 'عرض الكيو ار كود' : "View QR Code"}>
                      <IconButton
                        onClick={() => handleViewQR(table)}
                        sx={{ color: '#FFD700' }}
                        size="small"
                      >
                        <QrCodeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'ar' ? 'إعادة إنشاء الكود' : "Regenerate QR Code"}>
                      <IconButton
                        onClick={() => handleRegenerateQR(table)}
                        sx={{ color: '#4CAF50' }}
                        size="small"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={language === 'ar' ? 'تعديل' : "Edit"}>
                      <IconButton
                        onClick={() => openEditDialog(table)}
                        sx={{ color: '#FFD700' }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={language === 'ar' ? 'حذف' : "Delete"}>
                      <IconButton
                        onClick={() => handleDeleteTable(table.id)}
                        sx={{ color: '#FF3D00' }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} dir={language === 'ar' ? 'rtl' : 'ltr'} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700' }}>
          {selectedTable ? language === 'ar' ? 'تعديل الطاولة' : 'Edit Table' : language === 'ar' ? 'إنشاء الطاولة' : 'Create New Table'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D' }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={language === 'ar' ? 'رقم الطاولة' : 'Table Number'}
              name="table_number"
              type="number"
              value={formData.table_number}
              onChange={handleInputChange}
              disabled={!!selectedTable}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            <TextField
              fullWidth
              label={language === 'ar' ? 'إسم الطاولة' : 'Table Name'}
              name="table_name"
              value={formData.table_name}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            <TextField
              fullWidth
              label={language === 'ar' ? 'السعة' : 'Capacity'}
              name="capacity"
              type="number"
              value={formData.capacity}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            
            {/* Location Selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الموقع' : 'Location'}</InputLabel>
              <Select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuItem value="Indoor">{language === 'ar' ? 'داخلية' : 'Indoor'}</MenuItem>
                <MenuItem value="Outdoor">{language === 'ar' ? 'خارجية' : 'Outdoor'}</MenuItem>
                <MenuItem value="VIP">{language === 'ar' ? 'مميزة' : 'VIP'}</MenuItem>
                <MenuItem value="Terrace">{language === 'ar' ? 'شرفة' : 'Terrace'}</MenuItem>
              </Select>
            </FormControl>

            {/* VIP Settings */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#1A1A1A', border: formData.is_vip ? '1px solid #9C27B0' : '1px solid #2D2D2D' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_vip"
                    checked={formData.is_vip}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#9C27B0',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#9C27B0',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiamondIcon sx={{ color: formData.is_vip ? '#9C27B0' : '#B3B3B3', fontSize: 20 }} />
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {language === 'ar' ? 'طاولة VIP' : 'VIP Table'}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, display: 'block' }}
              />
              
              {formData.is_vip && (
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'الحد الأدنى للطلب' : 'Minimum Order Amount'}
                  name="min_charge"
                  type="number"
                  value={formData.min_charge}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R.m</InputAdornment>,
                    sx: { color: '#FFFFFF' }
                  }}
                  InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                  helperText={language === 'ar' ? 'الحد الأدنى للطلب لهذه الطاولة' : 'Minimum order amount required for this table'}
                  FormHelperTextProps={{ sx: { color: '#B3B3B3' } }}
                />
              )}
            </Paper>

            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#FFD700',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#FFD700',
                    },
                  }}
                />
              }
              label={language === 'ar' ? 'مفعل' : 'Active'}
              sx={{ color: '#FFFFFF', mb: 2, display: 'block' }}
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_occupied"
                  checked={formData.is_occupied}
                  onChange={handleInputChange}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#FF3D00',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#FF3D00',
                    },
                  }}
                />
              }
              label={language === 'ar' ? 'مشغولة' : 'Occupied'}
              sx={{ color: '#FFFFFF', mb: 2, display: 'block' }}
            />
            <TextField
              fullWidth
              label={language === 'ar' ? 'ملاحظات' : 'Notes'}
              name="notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D' }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={selectedTable ? handleUpdateTable : handleCreateTable}
            variant="contained"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {selectedTable ? language === 'ar' ? 'تحديث' : 'Update' : language === 'ar' ? 'إنشاء' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={openBulkDialog} dir={language === 'ar' ? 'rtl' : 'ltr'} onClose={() => setOpenBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700' }}>
          {language === 'ar' ? 'إنشاء طاولات متعددة دفعة واحدة' : 'Bulk Create Tables'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D' }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={language === 'ar' ? 'رقم البداية' : 'Start Number'}
              name="startNumber"
              type="number"
              value={bulkData.startNumber}
              onChange={handleBulkInputChange}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            <TextField
              fullWidth
              label={language === 'ar' ? 'رقم النهاية' : 'End Number'}
              name="endNumber"
              type="number"
              value={bulkData.endNumber}
              onChange={handleBulkInputChange}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            <TextField
              fullWidth
              label={language === 'ar' ? 'السعة' : 'Capacity'}
              name="capacity"
              type="number"
              value={bulkData.capacity}
              onChange={handleBulkInputChange}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الموقع' : 'Location'}</InputLabel>
              <Select
                name="location"
                value={bulkData.location}
                onChange={handleBulkInputChange}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuItem value="Indoor">{language === 'ar' ? 'داخلي' : 'Indoor'}</MenuItem>
                <MenuItem value="Outdoor">{language === 'ar' ? 'خارجي' : 'Outdoor'}</MenuItem>
                <MenuItem value="VIP">{language === 'ar' ? 'مميزة' : 'VIP'}</MenuItem>
                <MenuItem value="Terrace">{language === 'ar' ? 'شرفة' : 'Terrace'}</MenuItem>
              </Select>
            </FormControl>
            
            {/* VIP Settings for Bulk */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#1A1A1A', border: bulkData.is_vip ? '1px solid #9C27B0' : '1px solid #2D2D2D' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_vip"
                    checked={bulkData.is_vip}
                    onChange={handleBulkInputChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#9C27B0',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#9C27B0',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DiamondIcon sx={{ color: bulkData.is_vip ? '#9C27B0' : '#B3B3B3', fontSize: 20 }} />
                    <Typography sx={{ color: '#FFFFFF' }}>
                      {language === 'ar' ? 'طاولات VIP' : 'VIP Tables'}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, display: 'block' }}
              />
              
              {bulkData.is_vip && (
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'الحد الأدنى للطلب' : 'Minimum Order Amount'}
                  name="min_charge"
                  type="number"
                  value={bulkData.min_charge}
                  onChange={handleBulkInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R.m</InputAdornment>,
                    sx: { color: '#FFFFFF' }
                  }}
                  InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                />
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D' }}>
          <Button onClick={() => setOpenBulkDialog(false)} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleBulkCreate}
            variant="contained"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {language === 'ar' ? 'إنشاء الطاولات' : 'Create Tables'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={openQRDialog} dir={language === 'ar' ? 'rtl' : 'ltr'} onClose={() => setOpenQRDialog(false)} maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700' }}>
          {language === 'ar' ? 'كود المسح' : 'QR Code'} - {selectedTable?.table_name}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D', textAlign: 'center' }}>
          {qrCodeImage && (
            <Box sx={{ p: 2 }}>
              <img 
                src={qrCodeImage} 
                alt={`QR Code for ${selectedTable?.table_name}`}
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  border: '2px solid #FFD700',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#FFFFFF'
                }} 
              />
              <Typography sx={{ color: '#B3B3B3', mt: 2 }}>
                URL: {selectedTable?.qr_code_url}
              </Typography>
              {selectedTable?.is_vip && selectedTable?.min_charge > 0 && (
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />}
                  sx={{ 
                    mt: 2, 
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    color: '#CE93D8',
                    border: '1px solid #9C27B0'
                  }}
                >
                  <Typography variant="body2">
                    {language === 'ar'
                      ? `طاولة VIP - الحد الأدنى للطلب: ${selectedTable.min_charge} ر.م`
                      : `VIP Table - Minimum order: ${selectedTable.min_charge} R.m`
                    }
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D' }}>
          <Button
            onClick={handleDownloadQR}
            startIcon={<DownloadIcon />}
            sx={{ color: '#FFD700' }}
          >
            {language === 'ar' ? 'تحميل' : 'Download'}
          </Button>
          <Button onClick={() => setOpenQRDialog(false)} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#FFD700' : '#FF3D00'}`,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#FFD700' : '#FF3D00',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CafeTables;